import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { QuizService } from '../../services/quiz.service';

@Component({
  standalone: true,
  selector: 'app-quiz',
  imports: [CommonModule, TranslateModule],
  templateUrl: './quiz.component.html',
  styleUrl: './quiz.component.scss',
})
export class QuizComponent {
  #quizService = inject(QuizService);

  /** Task description (e.g. from task.description) */
  description = input<string>('');

  /** Quiz ID to fetch questions for */
  quizId = input<string>('');

  /** Task ID for start/complete (optional) */
  taskId = input<string | null>(null);

  /** Emitted when the quiz is completed. Payload: { correctCount, total, isPerfect } */
  quizCompleted = output<{ correctCount: number; total: number; isPerfect: boolean }>();

  /** Emitted when the learner selects an incorrect answer */
  answerIncorrect = output<void>();

  questions = signal<ChallengeGen.Question[]>([]);
  currentIndex = signal(0);
  selectedAnswerId = signal<string | null>(null);
  checkedAnswerId = signal<string | null>(null);
  isCorrect = signal<boolean | null>(null);
  isLoading = signal(false);
  error = signal<string | null>(null);
  /** Count of correct answers (for completeTaskWithDetails) */
  correctAnswersCount = signal(0);

  /** Shown when last question is answered incorrectly */
  lastQuestionWrong = signal(false);

  currentQuestion = computed(() => {
    const qs = this.questions();
    const idx = this.currentIndex();
    return qs[idx] ?? null;
  });

  progressText = computed(() => {
    const total = this.questions().length;
    const current = this.currentIndex() + 1;
    return total > 0 ? `${current} of ${total}` : '';
  });

  canGoPrev = computed(() => this.currentIndex() > 0);

  canGoNext = computed(() => {
    const idx = this.currentIndex();
    const total = this.questions().length;
    return total > 0 && idx < total - 1;
  });

  constructor() {
    effect(
      () => {
        const id = this.quizId();
        if (id?.trim()) this.#loadQuestions(id);
      },
      { allowSignalWrites: true },
    );
  }

  async #loadQuestions(quizId: string): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const taskId = this.taskId();
      if (taskId) {
        try {
          await this.#quizService.startTask(taskId);
        } catch (startErr) {
          if (
            !(startErr instanceof Error) ||
            !startErr.message?.includes('already completed')
          ) {
            throw startErr;
          }
        }
      }
      const qs = await this.#quizService.getQuestions(quizId);
      this.questions.set(qs ?? []);
      this.currentIndex.set(0);
      this.selectedAnswerId.set(null);
      this.checkedAnswerId.set(null);
      this.isCorrect.set(null);
      this.lastQuestionWrong.set(false);
      this.correctAnswersCount.set(0);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : 'Failed to load questions');
    } finally {
      this.isLoading.set(false);
    }
  }

  selectAnswer(answer: ChallengeGen.Answer): void {
    if (this.checkedAnswerId()) return;
    this.selectedAnswerId.set(answer.id);
  }

  async checkAnswer(): Promise<void> {
    const q = this.currentQuestion();
    const answerId = this.selectedAnswerId();
    if (!q || !answerId) return;

    const isLastQuestion =
      this.currentIndex() === this.questions().length - 1;

    this.isLoading.set(true);
    this.error.set(null);
    try {
      const result = await this.#quizService.checkAnswer(q.id, answerId);
      this.checkedAnswerId.set(answerId);
      const correct = result.isCorrect === true;
      this.isCorrect.set(correct);
      if (correct) {
        this.correctAnswersCount.update((n) => n + 1);
      }

      if (correct && isLastQuestion) {
        const taskId = this.taskId();
        const total = this.questions().length;
        const correctCount = this.correctAnswersCount();
        if (taskId) {
          await this.#quizService.completeTaskWithDetails(taskId, {
            quizCorrectAnswers: correctCount,
            quizTotalQuestions: total,
            isPerfectQuiz: correctCount === total,
          });
        }
        this.quizCompleted.emit({
          correctCount,
          total,
          isPerfect: correctCount === total,
        });
      } else if (!correct) {
        this.lastQuestionWrong.set(true);
        this.answerIncorrect.emit();
      }
    } catch (e) {
      this.error.set(
        e instanceof Error ? e.message : 'Failed to check answer',
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  goPrev(): void {
    if (!this.canGoPrev()) return;
    this.currentIndex.update((i) => i - 1);
    this.selectedAnswerId.set(null);
    this.checkedAnswerId.set(null);
    this.isCorrect.set(null);
    this.lastQuestionWrong.set(false);
  }

  goNext(): void {
    if (!this.canGoNext()) return;
    this.currentIndex.update((i) => i + 1);
    this.selectedAnswerId.set(null);
    this.checkedAnswerId.set(null);
    this.isCorrect.set(null);
  }

  isAnswerSelected(answer: ChallengeGen.Answer): boolean {
    return this.selectedAnswerId() === answer.id;
  }

  isAnswerChecked(answer: ChallengeGen.Answer): boolean {
    return this.checkedAnswerId() === answer.id;
  }

  isAnswerCorrect(answer: ChallengeGen.Answer): boolean {
    return this.isAnswerChecked(answer) && this.isCorrect() === true;
  }
}
